import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./AddressMap.module.css";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line
import "mapbox-gl/dist/mapbox-gl.css";

// icons
import LocationIcon from "./../../../../../../assets/images/icons/pin-red.png";
import { ReactComponent as CurrentLocationIcon } from "../../../../../../assets/svg/current-location.svg";

import { useTranslation } from "react-i18next";
import { Button, Drawer, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  ADDRESS_ACTION_TYPE,
  CLOSE_FORM_MODAL,
  UPDATE_LOCATION,
} from "../../../../../../store/actions/address_actions";
import { Note } from "../../../../../../helbest";
import { LoadingOutlined } from "@ant-design/icons";
import { channelDetails, BaseChannel } from "../../../../../../base/env";

mapboxgl.accessToken =
  "pk.eyJ1IjoibWVocmFuNzlzYWRlZ2hpMiIsImEiOiJjbDNhNmY2ODcwMmluM2NsZWdlZzk0aWw4In0.xbk1STevQddVcoW_2_Vsqg";
mapboxgl.setRTLTextPlugin(
  "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js",
  null,
  true
);

const POSTAL_CODE_RULES = {
  "Saudi Arabia": {
    pattern: /^\d{5}$/,
    maxLength: 5,
    format: (value) => value.replace(/\D/g, "").slice(0, 5),
  },
  Oman: {
    pattern: /^\d{3}$/,
    maxLength: 3,
    format: (value) => value.replace(/\D/g, "").slice(0, 3),
  },
  default: {
    pattern: /^.+$/, // any non-empty value, no format restriction
    maxLength: 10,
    format: (value) => value,
  },
};

const getPostalCodeRule = (countryName) =>
  POSTAL_CODE_RULES[countryName] || POSTAL_CODE_RULES.default;

const AddressMap = ({ onSubmit }) => {
  const { address, status, formModalOpen } = useSelector(
    (state) => state.Address
  );
  const { channel, country_name } = useSelector((store) => store.Login);

  const postalCodeRule = getPostalCodeRule(country_name);
  const [postalCode, setPostalCode] = useState(address?.postalCode || "");
  const [postalCodeTouched, setPostalCodeTouched] = useState(false);

  const isPostalCodeValid = useMemo(
    () => postalCodeRule.pattern.test(postalCode),
    [postalCode, postalCodeRule]
  );

  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(14);
  const [streetAddress1, setStreetAddress1] = useState(
    address?.streetAddress1 || ""
  );
  const [streetAddress2, setStreetAddress2] = useState(
    address?.streetAddress2 || ""
  );
  const [city, setCity] = useState(address?.city || "");
  const dispatch = useDispatch();

  const mapContainer = useRef(null);
  const map = useRef(null);
  const userMarkerRef = useRef(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const { t } = useTranslation();
  const isDisable = useMemo(() => {
    if (
      streetAddress1.length < 1 ||
      streetAddress2.length < 1 ||
      city.length < 1 ||
      !isPostalCodeValid
    ) {
      return true;
    } else {
      return false;
    }
  }, [streetAddress1, streetAddress2, city, isPostalCodeValid]);

  useEffect(() => {
    setPostalCode("");
    setPostalCodeTouched(false);
  }, [country_name]);

  useEffect(() => {
    setStreetAddress1(address?.streetAddress1 || "");
    setStreetAddress2(address?.streetAddress2 || "");
    setCity(address?.city || "");
    setPostalCode(address?.postalCode || "");
  }, [JSON.stringify(address)]);

  const addUserLocationMarker = (latitude, longitude) => {
    if (map.current) {
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
      }

      const markerEl = document.createElement("div");
      markerEl.className = "user-location-marker";
      markerEl.style.width = "16px";
      markerEl.style.height = "16px";
      markerEl.style.borderRadius = "50%";
      markerEl.style.backgroundColor = "#4285F4";
      markerEl.style.border = "2px solid white";
      markerEl.style.boxShadow = "0 0 0 2px rgba(66, 133, 244, 0.3)";

      // Create and add the marker
      userMarkerRef.current = new mapboxgl.Marker(markerEl)
        .setLngLat([longitude, latitude])
        .addTo(map.current);
    }
  };

  const setUserLocation = () => {
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      Note.error(t("address_map.errors.geolocation_not_supported"));
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position.coords.latitude.toFixed(4));
        const longitude = Number(position.coords.longitude.toFixed(4));

        setIsLoadingLocation(false);

        updateLocation(latitude, longitude);
        addUserLocationMarker(latitude, longitude);
      },
      (error) => {
        let errorMessage;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t("address_map.errors.location_access_denied");
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t("address_map.errors.location_unavailable");
            break;
          case error.TIMEOUT:
            errorMessage = t("address_map.errors.geolocation_request_timeout");
            break;
          default:
            errorMessage = t("address_map.errors.location_failed");
        }

        Note.error(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const updateLocation = (lat, lng) => {
    map.current.flyTo({
      center: [lng, lat],
      zoom: 14,
      essential: true,
    });
  };

  const defaultMapCenter = [58.545284, 23.614328];
  const channelConfig =
    channelDetails && (channelDetails[channel] || channelDetails[BaseChannel]);
  const mapCenter =
    channelConfig && Array.isArray(channelConfig.mapCenter)
      ? channelConfig.mapCenter
      : defaultMapCenter;

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: mapCenter,
      zoom,
    });
    if (status === ADDRESS_ACTION_TYPE.EDIT) {
      dispatch(
        UPDATE_LOCATION({
          lng: address?.location.lat,
          lat: address?.location.lng,
        })
      );
      if (address) {
        updateLocation(
          Number(address.location.lat),
          Number(address.location.lng)
        );
      }
    } else if (status === ADDRESS_ACTION_TYPE.CREATE) {
      setUserLocation();
    }
  }, [formModalOpen]); // eslint-disable-line

  useEffect(() => {
    if (!map.current) return;
    map.current.on("moveend", () => {
      dispatch(
        UPDATE_LOCATION({
          lng: map.current.getCenter().lng,
          lat: map.current.getCenter().lat,
        })
      );
      setZoom(map.current.getZoom().toFixed(2));
    });
  }, [formModalOpen]); // eslint-disable-line

  return (
    <div className={styles.AddressMap}>
      <div
        ref={mapContainer}
        className={styles.MapContainer}
        style={{ pointerEvents: formModalOpen ? "none" : "all" }}
      />
      <Drawer
        open={formModalOpen}
        onClose={() => dispatch(CLOSE_FORM_MODAL())}
        placement="bottom"
        closable={false}
        height={360}
        style={{
          borderTopLeftRadius: "25px",
          borderTopRightRadius: "25px",
        }}
        styles={{
          body: {
            padding: "16px",
          },
        }}
      >
        <form className={styles.LocationDetailsContainer}>
          <h1 className="mb-2">
            {status === ADDRESS_ACTION_TYPE.EDIT
              ? t("cart.address_edit")
              : t("cart.address_add")}
          </h1>
          <div className={styles.LocationAddressContainer}>
            <h2>{t("address_map.address_line")}</h2>
            <input
              type="text"
              className={styles.LocationAddressText}
              placeholder={t("common.address")}
              maxLength={255}
              value={streetAddress1}
              onChange={(e) => setStreetAddress1(e.target.value)}
            />
          </div>
          <div className="flex justify-between items-center">
            <div className={styles.LocationAddressContainer}>
              <h2>{t("address_map.province")}</h2>
              <input
                type="text"
                className={styles.LocationAddressText}
                placeholder={t("address_map.province")}
                disabled
                value={streetAddress2}
                onChange={(e) => setStreetAddress2(e.target.value)}
              />
            </div>
            <div className={styles.LocationAddressContainer}>
              <h2>{t("address_map.city")}</h2>
              <input
                type="text"
                className={styles.LocationAddressText}
                placeholder={t("address_map.city")}
                maxLength={255}
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.LocationAddressContainer}>
            <h2>{t("address_map.postal_code")}</h2>
            <input
              type="text"
              className={styles.LocationAddressText}
              placeholder={t("address_map.postal_code")}
              maxLength={postalCodeRule.maxLength}
              value={postalCode}
              onChange={(e) => {
                setPostalCode(postalCodeRule.format(e.target.value));
              }}
              onBlur={() => setPostalCodeTouched(true)}
            />
            {postalCodeTouched && postalCode.length > 0 && !isPostalCodeValid && (
              <span className={styles.ErrorText}>
                {t("address_map.errors.invalid_postal_code")}
              </span>
            )}
          </div>
          <div className="flex center-content">
            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={loading}
              disabled={isDisable}
              style={{
                width: "100%",
                marginTop: "24px",
              }}
              onClick={(e) => {
                e.preventDefault();
                setLoading(true);
                onSubmit({
                  streetAddress1,
                  streetAddress2,
                  city,
                  postalCode,
                }).finally(() => {
                  setLoading(false);
                });
              }}
            >
              {status === ADDRESS_ACTION_TYPE.EDIT
                ? t("common.edit")
                : t("common.add")}
            </Button>
          </div>
        </form>
      </Drawer>
      <div className={styles.LocationsContainer}>
        <div
          onClick={setUserLocation}
          className={styles.CurrentLocationIconContainer}
        >
          {isLoadingLocation ? (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          ) : (
            <CurrentLocationIcon className={styles.CurrentLocationIcon} />
          )}
        </div>
      </div>
      <div className={styles.LocationIconContainer}>
        <img
          src={LocationIcon}
          className={styles.LocationIcon}
          alt="location-icon"
        />
      </div>
    </div>
  );
};

export default AddressMap;
